import React from 'react';
import {StripeError} from '@stripe/stripe-js';
import {useQueryClient} from '@tanstack/react-query';
import {ReservationPayment, QUERY_CACHE_KEYS} from '@chekin/core';
import {useReservation} from '../../context/reservation';
import {API} from '@chekin/api';
import {fetcher} from '@chekin/shared';
import {useIsMounted} from '@chekin/guestapp-ui-kit';

const getCheckExpiredPaymentsPayload = (reservation_id = '') => ({
  reservation_id,
});

type UseCheckExpiredPaymentsProps = {
  onExpired?: (payments: ReservationPayment[]) => void;
  onError?: (error: StripeError) => void;
  onSettled?: () => void;
};

type CheckExpiredPaymentsOptions = UseCheckExpiredPaymentsProps;
function useCheckExpiredPayments({
  onExpired,
  onError,
  onSettled,
}: UseCheckExpiredPaymentsProps = {}) {
  const isMounted = useIsMounted();
  const {reservationId} = useReservation();
  const queryClient = useQueryClient();

  const checkExpiredPayments = React.useCallback(
    async ({
      onExpired: onLocalExpired,
      onError: onLocalError,
      onSettled: onLocalSettled,
    }: CheckExpiredPaymentsOptions = {}) => {
      if (!reservationId) return;
      const payload = getCheckExpiredPaymentsPayload(reservationId);

      try {
        const data = await fetcher(API.payments.checkExpiredReservationPayments, payload);

        if (!isMounted.current) return;
        onLocalSettled?.();
        onSettled?.();
        if (data?.has_expired_payments) {
          await queryClient.refetchQueries({
            queryKey: [QUERY_CACHE_KEYS.paymentsCart],
          });
          onLocalExpired?.(data?.expired_payments);
          onExpired?.(data?.expired_payments);
          return true;
        }
      } catch (e) {
        const error = e as StripeError;
        if (error) {
          onLocalError?.(error);
          onError?.(error);
          return;
        }
      }

      return false;
    },
    [queryClient, reservationId, isMounted, onSettled, onExpired, onError],
  );

  return {checkExpiredPayments};
}

export {useCheckExpiredPayments};
